import { t } from "translations"

import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import Select from "@mui/material/Select"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import { ISearchFacet } from "data/searchManager/interfaces/ISearch"
import { getKeyedArray } from "lib/getKeyedArray"

import Icon from "Components/Icon/Icon"

export default function TopFacetSelect({
    facets,
    selectFacet,
    selectedFacet
}: {
    facets: ISearchFacet[]
    selectFacet: (label: string) => void
    selectedFacet: string
}) {
    return (
        <StyledFormControl color="secondary">
            <InputLabel>
                <Typography
                    variant="subtitle2"
                    color="black">
                    {t.search.filterSearch}
                </Typography>
            </InputLabel>

            <StyledSelect
                IconComponent={props => (
                    <Icon
                        name="expandMore"
                        color="secondary"
                        {...props}
                    />
                )}
                color="secondary"
                labelId="facet-select"
                value={selectedFacet}
                label={<Typography> {t.search.filterSearch}</Typography>}>
                <StyledMenuItem
                    onClick={() => selectFacet("")}
                    value={""}>
                    <Typography variant="subtitle2">
                        {t.search.resetFilter}
                    </Typography>
                </StyledMenuItem>
                {facets.map(facet => (
                    <StyledMenuItem
                        onClick={() => selectFacet(facet.label)}
                        value={facet.label}
                        key={facet.label}>
                        <Typography variant="subtitle2">
                            {facet.label}
                        </Typography>
                    </StyledMenuItem>
                ))}
            </StyledSelect>
        </StyledFormControl>
    )
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    width: "100%",
    [theme.breakpoints.up("sm")]: {
        width: 250
    }
}))

const StyledSelect = styled(Select)(({ theme }) => ({
    "& .MuiOutlinedInput-notchedOutline": {
        border: `${theme.spacing(0.25)} solid `,
        borderRadius: theme.shape.borderRadius,
        borderColor: theme.palette.secondary.main,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    }
}))

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    minHeight: theme.spacing(0),
    ":first-of-type": {
        color: theme.palette.secondary.main
    }
}))

export function FacetResult({
    facets,
    selectFacet,
    selectedFacet,
    showFacetLoading
}: {
    facets: ISearchFacet[]
    selectFacet: (label: string) => void
    selectedFacet: string
    showFacetLoading: boolean
}) {
    return (
        <StyledLeftDiv>
            <StyledLeftAside>
                {showFacetLoading ? (
                    <>
                        {getKeyedArray(10).map(key => (
                            <Skeleton
                                key={key}
                                height={52}
                            />
                        ))}
                    </>
                ) : (
                    <>
                        <Typography variant="subtitle2">
                            {t.search.filterSearch}
                        </Typography>
                        <StyledFacetsList>
                            <FormControl component="fieldset">
                                <RadioGroup
                                    aria-label={t.search.filterSearch}
                                    name="filter"
                                    value={selectedFacet}>
                                    <StyledFormControlLabel
                                        value={""}
                                        control={
                                            <StyledRadio color="secondary" />
                                        }
                                        label={
                                            <Typography variant="subtitle2">
                                                {t.search.resetFilter}
                                            </Typography>
                                        }
                                        onClick={() => selectFacet("")}
                                    />
                                    {facets.map(facet => (
                                        <StyledFormControlLabel
                                            key={facet.label}
                                            value={facet.label}
                                            control={
                                                <StyledRadio color="secondary" />
                                            }
                                            label={
                                                <Typography variant="subtitle2">
                                                    {facet.label}
                                                </Typography>
                                            }
                                            onClick={() =>
                                                selectFacet(facet.label)
                                            }
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </StyledFacetsList>
                    </>
                )}
            </StyledLeftAside>
        </StyledLeftDiv>
    )
}

const StyledLeftDiv = styled("div")(({ theme }) => ({
    position: "relative",
    padding: theme.spacing(2)
}))

const StyledLeftAside = styled("div")(({ theme }) => ({
    position: "sticky",
    top: theme.constants.headerHeight,
    display: "none",
    [theme.breakpoints.up("md")]: {
        display: "block"
    }
}))

const StyledFacetsList = styled("div")(({ theme }) => ({
    padding: theme.spacing(2),
    border: `${theme.spacing(0.25)} solid ${theme.palette.secondary.main} `,
    borderRadius: theme.shape.borderRadius
}))

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
    ":first-of-type": {
        color: theme.palette.secondary.dark,
        textDecoration: "underline",
        "&:hover": {
            textDecoration: "none"
        }
    }
}))

const StyledRadio = styled(Radio)(({ theme }) => ({
    padding: theme.spacing(0.5, 0.5, 0.5, 1)
}))
